import { TimelineWriteOnlyContext } from '../../context-provider';
import { EditorStarterItem } from '../../items/item-type';
import {
  AnimationType,
  AnimationProperty,
  ItemAnimation,
  DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  addOrReplaceAnimation,
  removeAnimationByType,
} from '../../items/animation/animation-item-type';

interface AddAnimationParams {
  timelineWriteContext: TimelineWriteOnlyContext;
  targetItemId: string;
  animationType: AnimationType;
  durationInSeconds?: number;
  // 이미 그 아이템이 선택된 상태에서 붙일 때(그룹 멤버 depth) true — selectedItems 를
  // 같은 내용의 새 배열로 갈아끼우는 churn(스크롤 복원 effect 재실행 등)을 피한다.
  preserveSelection?: boolean;
  // 다중 선택 일괄 적용 — 지정하면 targetItemId 대신 이 목록 전체에 추가한다(undo 1회).
  targetItemIds?: string[];
}

// 렌더러가 Animation style을 실제 소비하는 아이템만 허용한다.
export const canApplyAnimation = (item: EditorStarterItem): boolean => {
  return item.type !== 'audio' && item.type !== 'code';
};

// 아이템에 Animation 추가 (복수 Animation 지원)
export const addAnimation = ({
  timelineWriteContext,
  targetItemId,
  animationType,
  durationInSeconds = DEFAULT_ANIMATION_DURATION_IN_SECONDS,
  preserveSelection = false,
  targetItemIds,
}: AddAnimationParams): void => {
  timelineWriteContext.setState({
    groupPropertyEdit: 'animation',
    update: (state) => {
      const ids =
        targetItemIds && targetItemIds.length > 0
          ? targetItemIds
          : [targetItemId];

      let items = state.undoableState.items;
      let changed = false;

      for (const id of ids) {
        const targetItem = items[id];

        // 없는 아이템·적용 불가 타입(audio)은 건너뛴다
        if (!targetItem || !canApplyAnimation(targetItem)) {
          continue;
        }

        // 기존 animations 가져오기 (없으면 빈 배열)
        const existingAnimations = (
          'animations' in targetItem && Array.isArray(targetItem.animations)
            ? targetItem.animations
            : []
        ) as ItemAnimation[];

        // 새 animation 생성 (아이템별 별도 객체 — 참조 공유 방지)
        const newAnimation: ItemAnimation = {
          type: animationType,
          durationInSeconds,
        };

        // 충돌 처리 후 추가
        const updatedAnimations = addOrReplaceAnimation(
          existingAnimations,
          newAnimation,
        );

        if (!changed) {
          items = { ...items };
          changed = true;
        }
        // canApplyAnimation 이 audio 를 걸렀지만 타입은 안 좁혀져 명시 캐스트
        items[id] = {
          ...targetItem,
          animations: updatedAnimations,
        } as EditorStarterItem;
      }

      if (!changed) {
        return state;
      }

      return {
        ...state,
        undoableState: {
          ...state.undoableState,
          items,
        },
        // 선택 상태 업데이트 (그룹 멤버 추가는 그룹 선택을 유지한다)
        ...(preserveSelection ? {} : { selectedItems: [targetItemId] }),
      };
    },
    commitToUndoStack: true,
  });
};

// 특정 타입의 Animation 제거
export const removeAnimation = ({
  timelineWriteContext,
  targetItemId,
  animationType,
}: {
  timelineWriteContext: TimelineWriteOnlyContext;
  targetItemId: string;
  animationType: AnimationType;
}): void => {
  timelineWriteContext.setState({
    groupPropertyEdit: 'animation',
    update: (state) => {
      const targetItem = state.undoableState.items[targetItemId];

      // 아이템이 없으면 무시
      if (!targetItem) {
        return state;
      }

      // Animations가 없으면 무시
      if (
        !('animations' in targetItem) ||
        !Array.isArray(targetItem.animations)
      ) {
        return state;
      }

      const existingAnimations = targetItem.animations as ItemAnimation[];
      const updatedAnimations = removeAnimationByType(
        existingAnimations,
        animationType,
      );

      // animations가 비어있으면 속성 제거
      if (updatedAnimations.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { animations, ...itemWithoutAnimations } =
          targetItem as typeof targetItem & {
            animations: ItemAnimation[];
          };

        return {
          ...state,
          undoableState: {
            ...state.undoableState,
            items: {
              ...state.undoableState.items,
              [targetItemId]: itemWithoutAnimations as EditorStarterItem,
            },
          },
        };
      }

      return {
        ...state,
        undoableState: {
          ...state.undoableState,
          items: {
            ...state.undoableState.items,
            [targetItemId]: {
              ...targetItem,
              animations: updatedAnimations,
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });
};

// 모든 Animation 제거
export const removeAllAnimations = ({
  timelineWriteContext,
  targetItemId,
}: {
  timelineWriteContext: TimelineWriteOnlyContext;
  targetItemId: string;
}): void => {
  timelineWriteContext.setState({
    groupPropertyEdit: 'animation',
    update: (state) => {
      const targetItem = state.undoableState.items[targetItemId];

      // 아이템이 없으면 무시
      if (!targetItem) {
        return state;
      }

      // Animations가 없으면 무시
      if (!('animations' in targetItem) || !targetItem.animations) {
        return state;
      }

      // animations 속성 제거
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { animations, ...itemWithoutAnimations } =
        targetItem as typeof targetItem & {
          animations: unknown;
        };

      return {
        ...state,
        undoableState: {
          ...state.undoableState,
          items: {
            ...state.undoableState.items,
            [targetItemId]: itemWithoutAnimations as EditorStarterItem,
          },
        },
      };
    },
    commitToUndoStack: true,
  });
};

// Animation Duration 업데이트
export const updateAnimationDuration = ({
  timelineWriteContext,
  targetItemId,
  animationType,
  durationInSeconds,
}: {
  timelineWriteContext: TimelineWriteOnlyContext;
  targetItemId: string;
  animationType: AnimationType;
  durationInSeconds: number;
}): void => {
  timelineWriteContext.setState({
    groupPropertyEdit: 'animation',
    update: (state) => {
      const targetItem = state.undoableState.items[targetItemId];

      if (
        !targetItem ||
        !('animations' in targetItem) ||
        !Array.isArray(targetItem.animations)
      ) {
        return state;
      }

      const existingAnimations = targetItem.animations as ItemAnimation[];
      const updatedAnimations = existingAnimations.map((animation) =>
        animation.type === animationType
          ? { ...animation, durationInSeconds, timelineWindow:undefined }
          : animation,
      );

      return {
        ...state,
        undoableState: {
          ...state.undoableState,
          items: {
            ...state.undoableState.items,
            [targetItemId]: {
              ...targetItem,
              animations: updatedAnimations,
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });
};

// Animation Input Range 업데이트
export const updateAnimationInputRange = ({
  timelineWriteContext,
  targetItemId,
  animationType,
  property,
  inputRange,
}: {
  timelineWriteContext: TimelineWriteOnlyContext;
  targetItemId: string;
  animationType: AnimationType;
  property: AnimationProperty;
  inputRange: number[];
}): void => {
  timelineWriteContext.setState({
    groupPropertyEdit: 'animation',
    update: (state) => {
      const targetItem = state.undoableState.items[targetItemId];

      if (
        !targetItem ||
        !('animations' in targetItem) ||
        !Array.isArray(targetItem.animations)
      ) {
        return state;
      }

      const existingAnimations = targetItem.animations as ItemAnimation[];
      const updatedAnimations = existingAnimations.map((animation) => {
        if (animation.type !== animationType) return animation;

        return {
          ...animation,
          customInputRanges: {
            ...animation.customInputRanges,
            [property]: inputRange,
          },
        };
      });

      return {
        ...state,
        undoableState: {
          ...state.undoableState,
          items: {
            ...state.undoableState.items,
            [targetItemId]: {
              ...targetItem,
              animations: updatedAnimations,
            },
          },
        },
      };
    },
    commitToUndoStack: true,
  });
};
